import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { CoverageAssessmentAlert } from '@/features/subcontractor-bids/coverage/components/coverage-assessment-alert';
import { AnalysisStatus } from '@/features/workspaces/constants';
import { ProcurementBreadcrumbs } from '@/features/subcontractor-bids/components/procurement-breadcrumbs';
import { useEffect, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Icon } from '@/ui/components/icon';
import {
  GridTableIcon,
  Refresh01Icon,
  InformationCircleIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import { MainHeader, MainHeaderBackButton } from '@/ui/components/main';
import { H1 } from '@/ui/components/heading';
import { Tabs, TabsList, TabsTab } from '@/ui/components/tabs';
import {
  procurementComparisonQuery,
  procurementFindingsQuery,
  levelingRunQuery,
} from '@/features/subcontractor-bids/queries';
import { useRunLevelingAnalysis } from '@/features/subcontractor-bids/mutations';
import { useProcurementAnalysisReady } from '@/features/subcontractor-bids/helpers';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import { ProcurementScopeCoverageTable } from '@/features/subcontractor-bids/coverage/components/procurement-scope-coverage-table';
import { ProcurementComplianceCoverageTable } from '@/features/subcontractor-bids/coverage/components/procurement-compliance-coverage-table';
import { SubcontractorsFrameTabs } from '@/features/subcontractor-bids/coverage/components/subcontractors-frame-tabs';
import { Button } from '@/ui/components/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
} from '@/ui/components/alert-dialog';
import { useAtom } from 'jotai';
import { ResizablePanel, ResizablePanelGroup } from '@/ui/components/resizable';
import { InlinePreviewPanel } from '@/features/documents/preview/components/inline-preview-panel';
import {
  levelingFindingKeyAtom,
  subcontractorCoverageCellSelectionAtom,
} from '@/features/subcontractor-bids/coverage/store';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import { CoverageCellDetailsPanel } from '@/features/subcontractor-bids/coverage/components/coverage-cell-details-panel';
import { ReservationsDetailPanel } from '@/features/subcontractor-bids/reservations/components/reservations-detail-panel';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/leveling',
)({
  component: RouteComponent,
  loaderDeps: ({ search }) => ({ tab: search.tab, frame: search.frame }),
  loader: async ({ context, params, deps }) => {
    const [, { availableFrames = ['tender'] }] = await Promise.all([
      context.queryClient.query({
        ...procurementFindingsQuery(params.procurementId),
        staleTime: 'static',
      }),
      context.queryClient.query({
        ...procurementComparisonQuery(params.procurementId, 'tender'),
        staleTime: 'static',
      }),
    ]);
    if (deps.frame === 'tender') return;
    if (deps.tab === 'scope' && availableFrames.includes(deps.frame)) return;
    throw redirect({
      from: '/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/leveling',
      to: '/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/leveling',
      params,
      search: (prev) => ({ ...prev, frame: 'tender' }),
      replace: true,
    });
  },
  validateSearch: z.object({
    tab: z.enum(['scope', 'compliance']).default('scope').catch('scope'),
    frame: z.enum(['tender', 'work_package']).default('tender').catch('tender'),
    requirementId: z.string().optional(),
    subcontractorId: z.string().optional(),
  }),
});

function RouteComponent() {
  const { projectId, workspaceId, procurementId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();

  const [selection, setSelection] = useAtom(subcontractorCoverageCellSelectionAtom);
  const [selectedFindingKey, setSelectedFindingKey] = useAtom(levelingFindingKeyAtom);
  const [rerunDialogOpen, setRerunDialogOpen] = useState(false);

  useEffect(
    () => () => {
      setSelection(null);
      setSelectedFindingKey(null);
    },
    [setSelection, setSelectedFindingKey],
  );

  const { data: findingsData } = useSuspenseQuery(procurementFindingsQuery(procurementId));

  const { isReadyToAnalyse, notReadyTooltip } = useProcurementAnalysisReady();

  const { data: run } = useQuery(levelingRunQuery(procurementId));

  const isProcessing = run?.status === AnalysisStatus.IN_PROGRESS;

  const rerunAnalysis = useRunLevelingAnalysis();

  const selectedFinding = selectedFindingKey
    ? (findingsData.findings.find((f) => f.findingKey === selectedFindingKey) ?? null)
    : null;

  const switchTab = (value: string) => {
    setSelection(null);
    setSelectedFindingKey(null);
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        tab: value as 'scope' | 'compliance',
        ...(value === 'compliance' ? { frame: 'tender' as const } : {}),
      }),
    });
  };

  return (
    <>
      <Header>
        <HeaderBackButton />
        <ProcurementBreadcrumbs>
          <BreadcrumbPage>
            <Icon icon={GridTableIcon} />
            {t({ en: 'Check coverage', nb: 'Sjekk dekning' })}
          </BreadcrumbPage>
        </ProcurementBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <ResizablePanelGroup className="flex flex-col min-h-0">
        <ResizablePanel minSize="200px" className="flex flex-col min-h-0 @container">
          <IndexingStatusAlerts resourceId={procurementId} documentType="workPackage" waitForKg />
          <MainHeader className="z-5">
            <H1 className="items-center">
              <MainHeaderBackButton
                to="/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId"
                params={{ projectId, workspaceId, procurementId }}
              />
              {t({ en: 'Check coverage', nb: 'Sjekk dekning' })}
              <Tooltip>
                <TooltipTrigger
                  delay={0}
                  render={
                    <Icon
                      icon={InformationCircleIcon}
                      className="size-4 shrink-0 ml-1.5 mt-1 text-blue-11 cursor-help"
                    />
                  }
                />
                <TooltipPopup variant="popup" className="max-w-sm">
                  <div className="flex flex-col gap-1.5">
                    <span>
                      <span className="font-medium">
                        {t({ en: 'Scope coverage', nb: 'Omfangsdekning' })}
                      </span>{' '}
                      {t({
                        en: 'shows which scope items each bidder included, excluded, or left open.',
                        nb: 'viser hvilke omfangsposter hver tilbyder inkluderte, ekskluderte eller utelot.',
                      })}
                    </span>
                    <span>
                      <span className="font-medium">
                        {t({ en: 'Compliance coverage', nb: 'Samsvarsdekning' })}
                      </span>{' '}
                      {t({
                        en: 'shows how each bid meets pack obligations and requirements.',
                        nb: 'viser hvordan hvert tilbud oppfyller pakkens forpliktelser og krav.',
                      })}
                    </span>
                  </div>
                </TooltipPopup>
              </Tooltip>
            </H1>
            <Tooltip>
              <TooltipTrigger
                delay={0}
                render={
                  <Button
                    kind="with-icon"
                    variant="secondary"
                    className="max-md:w-full"
                    disabled={isProcessing || !isReadyToAnalyse}
                    onClick={() => setRerunDialogOpen(true)}
                  >
                    <Icon icon={Refresh01Icon} />
                    {t(common.reRunAnalysis)}
                  </Button>
                }
              />
              {!isReadyToAnalyse && <TooltipPopup>{notReadyTooltip}</TooltipPopup>}
            </Tooltip>
          </MainHeader>
          <Tabs value={tab} onValueChange={switchTab}>
            <TabsList className="px-x">
              <TabsTab value="scope">{t({ en: 'Scope coverage', nb: 'Omfangsdekning' })}</TabsTab>
              <TabsTab value="compliance">
                {t({ en: 'Compliance coverage', nb: 'Samsvarsdekning' })}
              </TabsTab>
            </TabsList>
          </Tabs>
          {tab === 'scope' && <SubcontractorsFrameTabs />}
          <CoverageAssessmentAlert />
          {tab === 'scope' ? (
            <ProcurementScopeCoverageTable />
          ) : (
            <ProcurementComplianceCoverageTable />
          )}
        </ResizablePanel>
        <ReservationsDetailPanel
          open={!!selectedFinding}
          reservation={selectedFinding}
          onClose={() => setSelectedFindingKey(null)}
        />
        {!selectedFinding && selection ? (
          <CoverageCellDetailsPanel key={selection.requirementId} />
        ) : null}
        <InlinePreviewPanel />
      </ResizablePanelGroup>
      <AlertDialog open={rerunDialogOpen} onOpenChange={setRerunDialogOpen}>
        <AlertDialogPopup>
          <AlertDialogTitle>{t(common.reRunAnalysis)}?</AlertDialogTitle>
          <AlertDialogDescription>{t(common.reRunAnalysisDescription)}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rerunAnalysis.isPending} />
            <Button
              onClick={() =>
                rerunAnalysis.mutate(
                  { isRerun: true },
                  { onSuccess: () => setRerunDialogOpen(false) },
                )
              }
              pending={rerunAnalysis.isPending}
            >
              {t(common.reRunAnalysis)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}
